import CapacityElectricityDTO from "features/contracts/dtos/CapacityElectricityDTO";
import NumberFormatter from "features/shared/components/formatters/NumberFormatter";
import { formatNumber } from "features/shared/helpers/Formatters";
import React from "react";
import styles from './reservedCapacity.module.scss';
import CapacityTypeLabel from './CapacityTypeLabel';
import { useTranslation } from "react-i18next";
import RenderWrapper from "features/shared/components/renderWrapper/RenderWrapper";

interface Props {
  capacity: CapacityElectricityDTO;
  showTypeLabel?: boolean;
  bindField?: any;
  inputName?: string;
  onBlur?:(e: React.FocusEvent<HTMLInputElement>, capacity: CapacityElectricityDTO) => void;
  disabledInput?: boolean;
  error?: string;
}

const ReservedCapacityElectricity = ({
  capacity,
  showTypeLabel,
  bindField,
  inputName,
  onBlur,
  disabledInput = false,
  error
}: Props) => {
  // translations
  const tLabels = useTranslation(["contracts\\contracts"]).t;

  // helpers
  const getInputLabel = () => {
    if (hasInputView()) {
      return tLabels("reservedCapacityElectricity.newMonthlyCapacity");
    } else {
      return tLabels("reservedCapacityElectricity.total");
    }
  }

  const hasInputView = () => !!(inputName && bindField);

  // handlers
  const blurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    if (onBlur) {
      onBlur(e, capacity);
    }
  }

  // sub-renders
  const renderNumber = (amount: number) => {
    if (amount) {
      return (
        <>
          <NumberFormatter amount={amount} decimalPlaces={3} />
          <span className="d-sm-none small"> MW</span>
        </>
      );
    } else {
      return <>-</>
    }
  };

  const renderInput = () => {
    if (hasInputView()) {
      if (!disabledInput) {
        return (
          <input 
            className={`form-control ${styles.capacityInput}${error ? " border-danger" : ""}`}
            onBlur={blurHandler}
            {...bindField(inputName)} 
          />
        );
      } else {
        return (
          <input
            className={`form-control ${styles.capacityInput}`}
            disabled
            value={formatNumber(capacity.monthlyCapacity!, 3)}
          />
        );
      }   
    } else {
      return renderTotalCapacity();
    }
  }

  const renderError = () => {
    if (error) {
      return (
        <div className="d-block invalid-feedback text-right">
          {error}
        </div>
      );
    }
  }

  const renderTotalCapacity = () => {
    let monthlyCapacity = capacity.monthlyCapacity ?? 0;
    let yearlyCapacity = capacity.yearlyCapacity ?? 0;
    
    return renderNumber(monthlyCapacity + yearlyCapacity);
  }

  const renderTypeCol = () => {
    if (showTypeLabel) {
      return (
        <div className={`col-sm-auto mb-1 mb-sm-0 text-right ${styles.typeCol}`}>
          <div className="d-flex d-sm-block justify-content-between">
            <div className="d-sm-none text-muted pr-2">
              {tLabels("reservedCapacityElectricity.state")}
            </div>
            <div>
              <CapacityTypeLabel type={capacity.type!} />
            </div>
          </div>
        </div>
      );
    } 
  }

  const render15Mincol = () => { 
    return (
      <div className="d-none d-lg-block col text-center">
        {renderNumber(capacity.maxOf15Min!)}
      </div>
    );
  }

  // render
  return (
    <>
      <div className="row no-gutters align-items-center">
        <div className={`col-sm-auto mb-1 mb-sm-0 ${styles.periodCol}`}>
          <div className="d-flex d-sm-block justify-content-between">
            <div className="d-sm-none text-muted pr-2">
              {tLabels("reservedCapacityElectricity.timePeriod")}
            </div>
            <div>
              {capacity.month}/{capacity.year}
            </div>
          </div>
        </div>
        <div className="col-sm text-center mb-1 mb-sm-0">
          <div className="d-flex d-sm-block justify-content-between">
            <div className="d-sm-none text-muted pr-2">
              {tLabels("reservedCapacityElectricity.reservedPower")}
            </div>
            <div>
              {renderNumber(capacity.reserverPower!)}
            </div>
          </div>
        </div>
        <div className="col-sm text-center mb-1 mb-sm-0">
          <div className="d-flex d-sm-block justify-content-between">
            <div className="d-sm-none text-muted pr-2">
              {tLabels("reservedCapacityElectricity.yearlyCapacity")}
            </div>
            <div>
              {renderNumber(capacity.yearlyCapacity!)}
            </div>
          </div>
        </div>
        <div className="col-sm text-center mb-1 mb-sm-0">
          <div className="d-flex d-sm-block justify-content-between">
            <div className="d-sm-none text-muted pr-2">
              {tLabels("reservedCapacityElectricity.monthlyCapacity")}
            </div>
            <div>
              {renderNumber(capacity.monthlyCapacity!)}
            </div>
          </div>
        </div>
        <RenderWrapper isVisible={() => hasInputView()}>
          {render15Mincol()}
        </RenderWrapper>
        <div className="col-sm text-center mb-1 mb-sm-0">
          <div className="d-flex d-sm-block align-items-center justify-content-between">
            <div className="d-sm-none text-muted pr-2">
              {getInputLabel()}
            </div>
            <div>
              {renderInput()}
            </div>
          </div>
        </div>
        <RenderWrapper isVisible={() => !hasInputView()}>
          {render15Mincol()}
        </RenderWrapper>
        {renderTypeCol()}
      </div>
      {renderError()}
    </>
  );
};

export default ReservedCapacityElectricity;
